/**
 * Prepared Turntable PoC — reversible Viewer adapter spike.
 *
 * NOT production infrastructure. Gated behind:
 *   ?pocPreparedTurntable=1
 * or
 *   window.__IVANOV_POC_PREPARED_TURNTABLE__ = true
 *
 * When enabled and the opened diamond matches the prepared control
 * (ES-AA-08813), replaces raw Nivoda v360 with the local prepared
 * manifest so Frame Engine resolves:
 *   manifest.presentation.frames[index].url
 * instead of:
 *   ${v360.url}/${index}.jpg
 *
 * Remove this file + its liquid script tag to fully reverse the spike.
 */
(function (global) {
  "use strict";

  const CONTROL_IDS = {
    sourceId: "DIAMOND/07f72ee2-1034-4b39-90ed-208750add31f",
    certNumber: "ES-AA-08813",
  };

  const DEFAULT_MANIFEST_URL =
    "http://127.0.0.1:8767/ES-AA-08813/manifest.json";

  let cachedManifest = null;
  let loadPromise = null;

  function isEnabled() {
    if (global.__IVANOV_POC_PREPARED_TURNTABLE__ === true) return true;
    try {
      const params = new URLSearchParams(global.location && global.location.search);
      return params.get("pocPreparedTurntable") === "1";
    } catch (_) {
      return false;
    }
  }

  function matchesControl(diamond) {
    if (!diamond) return false;
    if (diamond.id === CONTROL_IDS.sourceId) return true;
    const cert =
      diamond.certNumber ||
      (diamond.certificate && diamond.certificate.certNumber) ||
      null;
    if (cert && String(cert).indexOf("ES-AA-08813") !== -1) return true;
    // Staging redacts cert numbers; also match by exact v360 URL host path
    // fragment used for ES-AA-08813.
    const url =
      (diamond.v360 && diamond.v360.url) ||
      (diamond.certificate &&
        diamond.certificate.v360 &&
        diamond.certificate.v360.url) ||
      "";
    return typeof url === "string" && url.indexOf("RVMtQUEtMDg4MTM") !== -1;
  }

  // Real R2 Manifest Integration (Stage 5A): the local /tmp fixture
  // (prepare-one.mjs) writes each frame with an already-absolute `url`,
  // but the real published R2 manifest instead gives each frame a
  // relative `ref` (e.g. "frames/000.webp") to be resolved against the
  // manifest's own location. Frame Engine's contract (buildFrameUrl)
  // only ever reads an already-absolute frames[i].url — this is the one
  // seam that must turn `ref` into that absolute url before handing
  // frames off. Prefers an explicit `url` when present (old fixture
  // shape unchanged); only resolves `ref` when `url` is absent (real R2
  // manifest shape). Returns null (never a broken string) if neither is
  // usable, which correctly makes resolveFrameMedia() reject the whole
  // frames[] array exactly as it already does for any incomplete entry.
  function resolveFrameUrl(frame, manifestUrl) {
    if (frame && typeof frame.url === "string" && frame.url.length > 0) {
      return frame.url;
    }
    if (frame && typeof frame.ref === "string" && frame.ref.length > 0 && manifestUrl) {
      try {
        return new URL(frame.ref, manifestUrl).href;
      } catch (_) {
        return null;
      }
    }
    return null;
  }

  function manifestToMedia(manifest, manifestUrl) {
    if (!manifest || !manifest.presentation || !Array.isArray(manifest.presentation.frames)) {
      return null;
    }
    const frames = manifest.presentation.frames;
    return {
      // Keep a stable base url for isPlayable / debugging; Frame Engine
      // prefers frames[i].url when present.
      url: "poc-prepared://" + (manifest.source && manifest.source.certNumber),
      frame_count: manifest.presentation.frameCount || frames.length,
      top_index: manifest.presentation.topIndex != null ? manifest.presentation.topIndex : 0,
      frames: frames.map(function (f) {
        return {
          url: resolveFrameUrl(f, manifestUrl),
          sourceIndex: f.sourceIndex,
        };
      }),
      __pocPrepared: true,
      __pocManifest: manifest,
    };
  }

  let cachedManifestUrl = null;

  function loadManifest(url) {
    if (cachedManifest) return Promise.resolve(cachedManifest);
    if (loadPromise) return loadPromise;
    const manifestUrl =
      (global.__IVANOV_POC_MANIFEST_URL__ || url || DEFAULT_MANIFEST_URL);
    loadPromise = fetch(manifestUrl, { cache: "no-store" })
      .then(function (res) {
        if (!res.ok) throw new Error("PoC manifest HTTP " + res.status);
        return res.json();
      })
      .then(function (json) {
        if (!json || json.status !== "ready") {
          throw new Error("PoC manifest not ready");
        }
        cachedManifest = json;
        cachedManifestUrl = manifestUrl;
        return json;
      })
      .catch(function (err) {
        loadPromise = null;
        console.warn("[v360-poc] prepared manifest unavailable:", err);
        return null;
      });
    return loadPromise;
  }

  /**
   * Returns a Promise resolving to either prepared media or the original
   * v360 object. Never throws — falls back to raw on any failure.
   */
  function maybePrepareV360(diamond, v360) {
    if (!isEnabled() || !v360 || !matchesControl(diamond)) {
      return Promise.resolve(v360);
    }
    return loadManifest().then(function (manifest) {
      const prepared = manifestToMedia(manifest, cachedManifestUrl);
      if (!prepared) return v360;
      console.info(
        "[v360-poc] using prepared turntable for",
        CONTROL_IDS.certNumber,
        prepared.frame_count + " frames"
      );
      return prepared;
    });
  }

  global.DiamondV360PocAdapter = {
    isEnabled: isEnabled,
    matchesControl: matchesControl,
    maybePrepareV360: maybePrepareV360,
    loadManifest: loadManifest,
    CONTROL_IDS: CONTROL_IDS,
    // Exposed for the Stage 5A real-manifest integration test harness
    // only (assets/diamond-v360-poc-adapter.real-manifest.test.js) — not
    // used by production call sites, which only ever go through
    // maybePrepareV360().
    resolveFrameUrl: resolveFrameUrl,
    manifestToMedia: manifestToMedia,
  };
})(window);
